import { action, computed, observable, runInAction } from "mobx";

import { BetSlipShare } from "@models/BetSlipShare";
import { getEventIdsFromMyBets } from "@utils/my-bets/helpers";
import { getCurrentCulture, UserTypes } from "@utils";
import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { ActiveEventsStore } from "./ActiveEventsStore";
import { LoaderStore } from "../common";
import { CashoutStore } from "./CashoutStore";
import { ReuseBetSlipStore } from "./ReuseBetSlipStore";
import { BetCancelStore } from "./BetCancelStore";
import ShareBetSlipStore from "./ShareBetSlipStore";
import { RootStore } from "../types";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const BetSlipService = lazy(loadFailPath, () =>import("@services/bet-slip/BetSlipService"))

export class BetSlipShareDetails {
	@observable bet: BetSlipShare | null;
	@observable isFetchingData: boolean;
	@observable isStoreInitialized: boolean;
	activeEventsStore: ActiveEventsStore;
	betCancelStore: BetCancelStore;
	cashoutStore: CashoutStore;
	loaderStore: LoaderStore;
	location: "BetSlipShareDetails";
	reuseBetSlipStore: ReuseBetSlipStore;
	shareBetSlipStore: ShareBetSlipStore;
	type = "BetSlipShareDetails";

	activeAccountOwner = UserTypes.PRIMARY;
	betCombinationsMap = observable.map();

	constructor(rootStore: RootStore, account: UserTypes = UserTypes.PRIMARY) {
		this.activeEventsStore = new ActiveEventsStore(rootStore);
		this.activeAccountOwner = account;

		this.reuseBetSlipStore = new ReuseBetSlipStore(rootStore);
		this.shareBetSlipStore = new ShareBetSlipStore(rootStore);
		this.loaderStore = new LoaderStore();
	}
	@computed get isLoading() {
		return this.loaderStore.isLoading;
	}

	@computed get isEmpty() {
		return (
			this.isStoreInitialized && !this.isFetchingData && this.bet == null
		);
	}
	@action.bound
	async getSharedBetSlipDetails(slipNumber: string) {
		this.isFetchingData = true;
		this.loaderStore.suspend();
		try {
			const bet = await (
				await BetSlipService
			).default.getSharedBetSlip(slipNumber);

			const eventIdsFromBet = getEventIdsFromMyBets([bet]);
			this.activeEventsStore.fetchActiveEvents(eventIdsFromBet);

			runInAction(() => {
				this.bet = bet;
				this.isFetchingData = false;
				this.isStoreInitialized = true;
			});
		} catch (error) {
			runInAction(() => {
				console.error(error);
				this.isFetchingData = false;
				this.isStoreInitialized = true;
			});
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async initBetCombinationForBetSlip(betSlipId: string) {
		if (this.betCombinationsMap.has(betSlipId)) {
			return;
		}

		this.betCombinationsMap.set(betSlipId, null);

		const combinations = await (
			await BetSlipService
		).default.getSystemCombinations(betSlipId, this.activeAccountOwner);

		this.betCombinationsMap.set(betSlipId, combinations);
	}

	@action.bound
	onDispose() {
		this.bet = null;
	}
}
